import { Button, IconButton } from "@etain/ui";
import type { NavigationItemId } from "@etain/domain";
import { ArrowDown, ArrowUp, type LucideIcon } from "lucide-react";
import { useRef, useState, type RefObject } from "react";
import { moveNavigationItem, orderNavigationItems } from "../lib/navigation-order.js";
import { FormDialog } from "./FormDialog.js";

export function NavigationOrderDialog({
  open,
  onOpenChange,
  items,
  order,
  defaultOrder,
  onSave,
  loading,
  loadError,
  busy,
  onRetry,
  restoreFocusRef,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  items: { id: NavigationItemId; label: string; icon: LucideIcon }[];
  order: NavigationItemId[];
  defaultOrder: NavigationItemId[];
  onSave: (order: NavigationItemId[]) => Promise<void>;
  loading: boolean;
  loadError: boolean;
  busy: boolean;
  onRetry: () => void;
  restoreFocusRef: RefObject<HTMLElement | null>;
}) {
  const loaded = !loading && !loadError;
  const list = useRef<HTMLOListElement>(null);
  const [editor, setEditor] = useState({
    open,
    loaded,
    draft: order,
    initial: order,
    error: "",
    message: "",
  });
  if (editor.open !== open || (open && loaded && !editor.loaded)) {
    setEditor({ open, loaded, draft: order, initial: order, error: "", message: "" });
  }
  const orderedItems = orderNavigationItems(items, editor.draft);
  const changed = editor.draft.join("\n") !== editor.initial.join("\n");

  function move(id: NavigationItemId, direction: -1 | 1) {
    const draft = moveNavigationItem(
      editor.draft,
      items.map((item) => item.id),
      id,
      direction,
    );
    const position = orderNavigationItems(items, draft).findIndex((item) => item.id === id) + 1;
    const label = items.find((item) => item.id === id)?.label;
    setEditor({
      ...editor,
      draft,
      error: "",
      message: `${label}: Position ${position} von ${items.length}.`,
    });
    // A moved button can become disabled at a boundary. Keep keyboard focus on this row.
    queueMicrotask(() => {
      const row = list.current?.querySelector(`[data-navigation-id="${id}"]`);
      const preferred = row?.querySelector<HTMLButtonElement>(`[data-direction="${direction}"]`);
      (preferred?.disabled
        ? row?.querySelector<HTMLButtonElement>("button:not(:disabled)")
        : preferred
      )?.focus();
    });
  }

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Menüreihenfolge anpassen"
      description="Verschieben Sie die Hauptmenüpunkte mit den Pfeilen. Ihre Auswahl wird in Ihrem Benutzerkonto gespeichert und gilt auf allen Geräten. CRM wird mit seinen Unterpunkten verschoben."
      size="small"
      submitLabel="Reihenfolge speichern"
      dirty={changed}
      busy={busy}
      restoreFocusRef={restoreFocusRef}
      submitDisabled={!loaded}
      error={
        loadError ? (
          <>
            Die Menüreihenfolge konnte nicht geladen werden.{" "}
            <Button type="button" variant="quiet" onClick={onRetry}>
              Erneut laden
            </Button>
          </>
        ) : (
          editor.error || undefined
        )
      }
      onSubmit={(event) => {
        event.preventDefault();
        if (!loaded || busy) return;
        void onSave(editor.draft).catch(() => {
          setEditor({
            ...editor,
            error:
              "Die Reihenfolge konnte nicht gespeichert werden. Bitte versuchen Sie es erneut.",
          });
        });
      }}
      secondaryActions={
        <Button
          variant="quiet"
          type="button"
          disabled={!loaded || busy}
          onClick={() =>
            setEditor({
              ...editor,
              draft: [...defaultOrder],
              error: "",
              message:
                "Standardreihenfolge wiederhergestellt. Wählen Sie „Reihenfolge speichern“, um sie zu übernehmen.",
            })
          }
        >
          Standard wiederherstellen
        </Button>
      }
    >
      {loading ? <p role="status">Menüreihenfolge wird geladen …</p> : null}
      <ol ref={list} className="navigation-order" aria-label="Menüreihenfolge" hidden={!loaded}>
        {orderedItems.map((item, index) => {
          const Icon = item.icon;
          return (
            <li key={item.id} data-navigation-id={item.id}>
              <span className="navigation-order__position" aria-hidden="true">
                {index + 1}
              </span>
              <Icon size={19} aria-hidden="true" />
              <span className="navigation-order__label">{item.label}</span>
              <IconButton
                type="button"
                aria-label={`${item.label} nach oben`}
                disabled={index === 0 || busy}
                data-direction="-1"
                onClick={() => move(item.id, -1)}
              >
                <ArrowUp aria-hidden="true" />
              </IconButton>
              <IconButton
                type="button"
                aria-label={`${item.label} nach unten`}
                disabled={index === orderedItems.length - 1 || busy}
                data-direction="1"
                onClick={() => move(item.id, 1)}
              >
                <ArrowDown aria-hidden="true" />
              </IconButton>
            </li>
          );
        })}
      </ol>
      <p className="ui-sr-only" role="status">
        {editor.message}
      </p>
    </FormDialog>
  );
}
